import { useMemo, useState } from "react";
import { X, Scissors, Check } from "lucide-react";
import { availableMeasurements, adjustmentOptions, fmtIn, fmtAdj, CONFIRM_TEXT, CUSTOM_FIT_FEE } from "@/lib/alterations";

// Read-only summary of a customised item's measurements. Used in basket,
// checkout and order summary. Renders nothing for non-customised items.
export const CustomFitSummary = ({ customization, className = "", compact = false }) => {
  const ms = customization?.measurements || [];
  if (!ms.length) return null;
  return (
    <div className={`mt-2 ${className}`} data-testid="custom-fit-summary">
      <p className={`text-[11px] uppercase tracking-[0.12em] font-semibold text-pink flex items-center gap-1 ${compact ? "" : "mb-0.5"}`}>
        <Scissors className="w-3 h-3" /> Custom Fit
      </p>
      <ul className="text-xs text-gray-500 space-y-0.5">
        {ms.map((m) => (
          <li key={m.key}>
            {m.label}: <span className="text-charcoal">{fmtAdj(m.adjustment)}</span> &rarr; {fmtIn(m.final)}
          </li>
        ))}
      </ul>
      <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-dashed border-gray-200 text-xs" data-testid="custom-fit-fee">
        <span className="text-gray-500">Custom tailoring fee</span>
        <span className="font-semibold text-charcoal">+£{CUSTOM_FIT_FEE.toFixed(2)}</span>
      </div>
      <p className="text-[11px] text-gold mt-1" data-testid="custom-fit-dispatch">Made to order — allow 3–5 working days for dispatch.</p>
    </div>
  );
};

// Modal panel to configure the fit. Calls onApply(customization|null, confirmed).
export const CustomiseFitModal = ({ product, size, open, onClose, onApply, initial }) => {
  const rows = useMemo(() => availableMeasurements(product, size), [product, size]);
  const [adjustments, setAdjustments] = useState(() => {
    const seed = {};
    (initial?.measurements || []).forEach((m) => { seed[m.key] = m.adjustment; });
    return seed;
  });
  const [confirmed, setConfirmed] = useState(!!initial?.measurements?.length);

  if (!open) return null;

  const setAdj = (key, v) => setAdjustments((a) => ({ ...a, [key]: v }));
  const selected = rows
    .map((r) => ({ ...r, adjustment: adjustments[r.key] || 0 }))
    .filter((r) => r.adjustment !== 0)
    .map((r) => ({ key: r.key, label: r.label, standard: r.standard, adjustment: r.adjustment, final: r.standard + r.adjustment }));
  const isCustomised = selected.length > 0;
  const canApply = !isCustomised || confirmed;

  const apply = () => {
    if (!canApply) return;
    onApply(isCustomised ? { measurements: selected } : null, isCustomised && confirmed);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center" data-testid="customise-fit-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white w-full sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl shadow-xl">
        <div className="sticky top-0 bg-white flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-serif text-2xl flex items-center gap-2"><Scissors className="w-5 h-5 text-gold" /> Customise Fit</h3>
          <button onClick={onClose} aria-label="Close" data-testid="customise-fit-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-6 py-5">
          <p className="text-sm text-gray-500 mb-5">
            Adjust each measurement for your perfect fit (size <span className="font-medium text-charcoal">{size}</span>). Values are in inches. A <span className="font-medium text-charcoal">£{CUSTOM_FIT_FEE.toFixed(2)}</span> tailoring fee applies per customised garment, and customised items take <span className="font-medium text-charcoal">3–5 working days</span> to dispatch.
          </p>

          <div className="space-y-6">
            {rows.map((r) => {
              const adj = adjustments[r.key] || 0;
              const final = r.standard + adj;
              return (
                <div key={r.key} data-testid={`customise-${r.key}`}>
                  <div className="flex items-baseline justify-between mb-2">
                    <span className="text-sm font-medium text-charcoal">{r.label}</span>
                    <span className="text-xs text-gray-400">Standard: {fmtIn(r.standard)}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {adjustmentOptions(r.up, r.down).map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setAdj(r.key, v)}
                        data-testid={`customise-${r.key}-${v}`}
                        className={`px-3 py-2 text-sm rounded-md border transition-colors ${adj === v ? "border-pink text-pink bg-pink/5 font-medium" : "border-gray-200 text-gray-600 hover:border-charcoal"}`}
                      >
                        {fmtAdj(v)}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Final: <span className="text-charcoal font-medium" data-testid={`customise-${r.key}-final`}>{fmtIn(final)}</span></p>
                </div>
              );
            })}
          </div>

          {isCustomised && (
            <label className="flex items-start gap-3 mt-6 bg-surface border border-gray-200 rounded-xl px-4 py-3.5 cursor-pointer" data-testid="customise-confirm">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                data-testid="customise-confirm-checkbox"
                className="mt-0.5 w-4 h-4 accent-pink shrink-0"
              />
              <span className="text-xs text-charcoal leading-relaxed">{CONFIRM_TEXT}</span>
            </label>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 py-3 rounded-full border border-gray-200 text-sm hover:border-charcoal transition-colors">
            Cancel
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={!canApply}
            data-testid="customise-fit-apply"
            className="flex-1 py-3 rounded-full bg-pink text-white text-sm font-medium flex items-center justify-center gap-2 hover:bg-pink-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Check className="w-4 h-4" /> {isCustomised ? "Apply Custom Fit" : "Use Standard Fit"}
          </button>
        </div>
      </div>
    </div>
  );
};
